import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import "./AnimatedNav.css";

const AnimatedNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { scrollY } = useScroll();

  // Enhanced scroll-based background with better transparency transition
  const backgroundColor = useTransform(
    scrollY,
    [0, 100, 200],
    [
      "linear-gradient(135deg, rgba(74, 21, 105, 0.7) 0%, rgba(107, 44, 145, 0.7) 100%)",
      "linear-gradient(135deg, rgba(74, 21, 105, 0.85) 0%, rgba(107, 44, 145, 0.85) 100%)",
      "linear-gradient(135deg, rgba(74, 21, 105, 0.98) 0%, rgba(107, 44, 145, 0.98) 100%)",
    ]
  );

  const boxShadow = useTransform(
    scrollY,
    [0, 100, 200],
    [
      "0 2px 8px rgba(0,0,0,0.05)",
      "0 2px 16px rgba(0,0,0,0.1)",
      "0 4px 24px rgba(0,0,0,0.15)",
    ]
  );

  const backdropBlur = useTransform(
    scrollY,
    [0, 100],
    ["blur(10px)", "blur(15px)"]
  );

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/services", label: "Services" },
    { path: "/gallery", label: "Gallery" },
    { path: "/contact", label: "Contact" },
  ];

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen]);

  // Scroll to top on location change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <motion.nav
      className="animated-nav-container"
      style={{
        backgroundColor,
        boxShadow,
        backdropFilter: backdropBlur,
        WebkitBackdropFilter: backdropBlur,
      }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="animated-nav-content">
        <Link to="/" className="animated-nav-logo">
          <motion.img
            src="https://customer-assets.emergentagent.com/job_fashion-vite/artifacts/4lfzooeq_1764089903800-removebg-preview.png"
            alt="PSD Designers Logo"
            className="animated-logo-image"
            whileHover={{ scale: 1.05, rotate: [0, -2, 2, 0] }}
            transition={{ type: "spring", stiffness: 300 }}
          />
        </Link>

        <button
          className="animated-mobile-menu-btn"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </motion.div>
        </button>

        <motion.ul
          className={`animated-nav-links ${isOpen ? "nav-open" : ""}`}
          initial={false}
          animate={isOpen ? "open" : "closed"}
        >
          {navLinks.map((link, index) => (
            <motion.li
              key={link.path}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <Link
                to={link.path}
                className={
                  location.pathname === link.path
                    ? "animated-nav-link active"
                    : "animated-nav-link"
                }
                onClick={() => setIsOpen(false)}
              >
                <span className="nav-link-text">{link.label}</span>
                <motion.span
                  className="nav-underline"
                  animate={{
                    width: location.pathname === link.path ? "100%" : 0,
                  }}
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </motion.nav>
  );
};

export default AnimatedNav;
